import html2pdf from 'html2pdf.js';
import { flushSync } from 'react-dom';
import { createRoot, type Root } from 'react-dom/client';
import React from 'react';
import { ProcurementInvoicePageSheet } from '../components/ProcurementInvoicePdfTemplate';
import {
  PROCUREMENT_PDF_HEIGHT,
  PROCUREMENT_PDF_WIDTH,
  buildInvoicePages,
  type ProcurementInvoicePdfData,
} from './procurementInvoicePdf';

const PDF_OPTS = {
  margin: 0,
  html2canvas: {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    width: PROCUREMENT_PDF_WIDTH,
    windowWidth: PROCUREMENT_PDF_WIDTH,
    height: PROCUREMENT_PDF_HEIGHT,
    scrollX: 0,
    scrollY: 0,
    letterRendering: true,
    logging: false,
  },
  jsPDF: {
    unit: 'px' as const,
    format: [PROCUREMENT_PDF_WIDTH, PROCUREMENT_PDF_HEIGHT] as [number, number],
    orientation: 'portrait' as const,
    compress: true,
  },
};

async function waitForProcurementPdfRender(): Promise<void> {
  await new Promise<void>(resolve => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
  try {
    if (document.fonts?.ready) await document.fonts.ready;
  } catch {
    /* ignore */
  }
  await new Promise<void>(resolve => setTimeout(resolve, 100));
}

async function renderPageSheet(
  container: HTMLDivElement,
  rootRef: { current: Root | null },
  data: ProcurementInvoicePdfData,
  pageIndex: number
): Promise<HTMLElement> {
  const pages = buildInvoicePages(data.lines);
  const page = pages[pageIndex];
  if (!page) throw new Error('PDF 페이지 정보를 찾을 수 없습니다.');

  if (rootRef.current) {
    flushSync(() => {
      rootRef.current!.unmount();
    });
    rootRef.current = null;
  }

  rootRef.current = createRoot(container);
  flushSync(() => {
    rootRef.current!.render(
      React.createElement(ProcurementInvoicePageSheet, { data, page })
    );
  });

  await waitForProcurementPdfRender();

  const el = container.querySelector('.procurement-invoice-page-sheet');
  if (!(el instanceof HTMLElement)) {
    throw new Error('PDF 템플릿을 렌더링하지 못했습니다.');
  }
  return el;
}

async function pageToCanvas(pageElement: HTMLElement): Promise<HTMLCanvasElement> {
  const canvas = await html2pdf().set(PDF_OPTS).from(pageElement).toCanvas().get('canvas');
  if (!(canvas instanceof HTMLCanvasElement)) {
    throw new Error('PDF canvas 생성에 실패했습니다.');
  }
  return canvas;
}

export async function downloadProcurementInvoicePdf(
  data: ProcurementInvoicePdfData,
  filename: string
): Promise<void> {
  const pages = buildInvoicePages(data.lines);
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.width = `${PROCUREMENT_PDF_WIDTH}px`;
  container.style.height = `${PROCUREMENT_PDF_HEIGHT}px`;
  container.style.overflow = 'hidden';
  document.body.appendChild(container);

  const rootRef: { current: Root | null } = { current: null };

  try {
    const firstEl = await renderPageSheet(container, rootRef, data, 0);
    const pdf = await html2pdf().set(PDF_OPTS).from(firstEl).toPdf().get('pdf');

    for (let i = 1; i < pages.length; i++) {
      const pageEl = await renderPageSheet(container, rootRef, data, i);
      const canvas = await pageToCanvas(pageEl);
      pdf.addPage([PROCUREMENT_PDF_WIDTH, PROCUREMENT_PDF_HEIGHT], 'p');
      pdf.addImage(
        canvas.toDataURL('image/jpeg', 0.95),
        'JPEG',
        0,
        0,
        PROCUREMENT_PDF_WIDTH,
        PROCUREMENT_PDF_HEIGHT,
        undefined,
        'FAST'
      );
    }

    pdf.save(filename);
  } finally {
    if (rootRef.current) {
      flushSync(() => {
        rootRef.current!.unmount();
      });
    }
    document.body.removeChild(container);
  }
}

export function buildProcurementInvoiceFilename(
  shipmentDate: string,
  destinationName: string,
  vehicleNumber: string
): string {
  const date = String(shipmentDate).slice(0, 10);
  const dest = String(destinationName || '출고').replace(/[\\/:*?"<>|]/g, '_');
  const vehicle = String(vehicleNumber || '차량').replace(/[\\/:*?"<>|]/g, '_');
  return `송장_${dest}_${vehicle}_${date}.pdf`;
}
